/**
 * Kleda Client App - Timeline Component
 * Dunne voortgangslijn ("Stil premium") — altijd zichtbaar bovenaan de app
 *
 * Toont 5 stappen: Besteld → Ophalen → Reinigen → Afleveren → Klaar
 * als één regel labels boven een 3px progress-track.
 */

// =============================================================================
// TYPES
// =============================================================================

type TimelineStep = 'besteld' | 'ophalen' | 'reinigen' | 'afleveren' | 'klaar'

interface TimelineProps {
  status: string
  /** Zelf-halen-en-brengen-pad: wisselt de labels van stap 2 en 4 */
  zelf?: { brengen: boolean; halen: boolean }
  className?: string
  /** Toon "Vervolg op uw klacht"-pill boven de timeline. Optioneel. */
  vervolgOpKlacht?: { klachtNummer: string; onClick?: () => void }
}

// =============================================================================
// STATUS MAPPING
// =============================================================================

function getTimelineStep(status: string): TimelineStep {
  const statusMap: Record<string, TimelineStep> = {
    // Ophalen fase
    'Ophalen plannen': 'ophalen',
    'Ophaalvoorstel gedaan': 'ophalen',
    'Ophaalvoorstel geen reactie': 'ophalen',
    'Ophalen ingepland': 'ophalen',
    'Opgehaald': 'reinigen',

    // Reinigen fase
    'Aan het reinigen': 'reinigen',
    'Kwaliteitscontrole': 'reinigen',

    // Afleveren fase
    'Terugbrengen plannen': 'afleveren',
    'Aflevervoorstel gedaan': 'afleveren',
    'Terugbrengen ingepland': 'afleveren',

    // Klaar
    'Teruggebracht': 'klaar',
    'Afgerond': 'klaar',

    // Zelf-halen-en-brengen-pad: zelfde stappen, andere labels (zie labels())
    'Wordt gebracht': 'ophalen',
    'Zelf gebracht': 'reinigen',
    'Wordt opgehaald': 'afleveren',
    'Zelf opgehaald': 'klaar',
  }

  return statusMap[status] || 'besteld'
}

/**
 * Stap 2 en 4 heten naar wie het doet. Route: wij halen en leveren. Zelf:
 * de klant brengt en haalt. Bij alleen zelf-halen zou "Ophalen → Reinigen →
 * Ophalen" verwarren, daarom daar "Afhalen".
 */
function labels(zelf?: { brengen: boolean; halen: boolean }): { id: TimelineStep; label: string }[] {
  const stap2 = zelf?.brengen ? 'Brengen' : 'Ophalen'
  const stap4 = zelf?.halen ? (zelf.brengen ? 'Ophalen' : 'Afhalen') : 'Afleveren'
  return [
    { id: 'besteld', label: 'Besteld' },
    { id: 'ophalen', label: stap2 },
    { id: 'reinigen', label: 'Reinigen' },
    { id: 'afleveren', label: stap4 },
    { id: 'klaar', label: 'Klaar' },
  ]
}

/** Vulpercentage van de track per actieve fase */
const PROGRESS_WIDTH: Record<TimelineStep, string> = {
  besteld: '8%',
  ophalen: '30%',
  reinigen: '52%',
  afleveren: '78%',
  klaar: '100%',
}

// =============================================================================
// TIMELINE COMPONENT
// =============================================================================

export function Timeline({ status, zelf, className = '', vervolgOpKlacht }: TimelineProps) {
  const currentStep = getTimelineStep(status)
  const STEPS = labels(zelf)
  const currentIndex = STEPS.findIndex(s => s.id === currentStep)

  return (
    <div className={`w-full ${className}`}>
      {/* Vervolg-WO indicator — boven de voortgangslijn */}
      {vervolgOpKlacht && (
        <div className="mb-md flex justify-center">
          <button
            type="button"
            onClick={vervolgOpKlacht.onClick}
            className="inline-flex items-center gap-xs px-md py-xs rounded-full bg-red-50 border border-red-200 text-red-700 text-body-sm font-medium hover:bg-red-100 transition-colors"
          >
            <span>↩ Vervolg op uw klacht</span>
            <span className="text-red-500">{vervolgOpKlacht.klachtNummer}</span>
          </button>
        </div>
      )}

      {/* Labels — één regel */}
      <div className="flex justify-between mb-xs">
        {STEPS.map((step, index) => {
          const isActive = index === currentIndex
          const isFuture = index > currentIndex
          return (
            <span
              key={step.id}
              className={`text-[11px] sm:text-[12px] leading-tight ${
                isActive
                  ? 'font-bold text-primary-dark'
                  : isFuture
                    ? 'text-ink-faint/60'
                    : 'text-ink-faint'
              }`}
            >
              {step.label}
            </span>
          )
        })}
      </div>

      {/* Track + fill */}
      <div className="h-[3px] bg-track rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-700 ease-out"
          style={{ width: PROGRESS_WIDTH[currentStep] }}
        />
      </div>
    </div>
  )
}
